import {
  AssignmentCategory,
  AssignmentStatus,
  AssignmentType,
  Entity,
} from "@/enums";
import { ResourceType } from "@/enums/ResourceType";
import { AmiraResources } from "@/models/AmiraResources.interface";
import { PlannerCurriculumLesson } from "@/features/planner/PlannerTypes";
import {
  Assignment,
  AssignmentCRUDCategory,
  AssignmentManifest,
  BatchAddAssignmentInput,
  BatchUpdateAssignmentInput,
  CreateAssignmentInput,
  CRUDAssignmentQueue,
  Skill,
} from "@/models";
import { isBefore, startOfDay } from "date-fns";
import isEqual from "react-fast-compare";

const getOtherSkillsTeacherResources = (
  lesson: PlannerCurriculumLesson,
  newStudentManifests?: { studentId: string; manifest: AssignmentManifest[] }[],
  removedTeacherResources?: string[],
  studentId?: string
) => {
  // Get skills that are being modified in this update
  const modifiedSkillIds = new Set(
    newStudentManifests?.flatMap(sm =>
      sm.manifest.flatMap(m => m.skills?.map(s => s.skillId) || [])
    ) || []
  );

  // Only include teacher resources from skills that belong to THIS lesson's main assignment
  // Filter out skills that don't belong to the current lesson being modified
  const lessonSkillIds = new Set(lesson.skills.map(s => s.skillId));

  // Get the student's current assignment to see what teacher resources they already have
  const studentAssignment = studentId
    ? lesson.studentAssignments.find(sa => sa.entityId === studentId)
    : null;
  const currentStudentResources = new Set(studentAssignment?.resources || []);

  // Include teacher resources for lesson skills EXCEPT the ones being modified
  // AND only if the student currently has those teacher resources
  return lesson.resources
    .filter(
      r => lessonSkillIds.has(r.skillId) && !modifiedSkillIds.has(r.skillId)
    )
    .flatMap(r =>
      r.resources
        .filter(res =>
          currentStudentResources.has(res.resourceUniqueId?.toString() || "")
        )
        .map(res => res.resourceUniqueId?.toString() || "")
    )
    .filter(id => !removedTeacherResources?.includes(id));
};

export const handleUnassignAssignments = (
  assignments: Partial<Assignment>[],
  userId: string
) => {
  const updateStatusList = assignments
    .filter(assign => assign.status === AssignmentStatus.ASSIGNED)
    .reduce((acc, cur) => {
      if (cur.assignmentId && cur.entityId && cur.entityType) {
        acc.push({
          assignmentId: cur.assignmentId,
          entityId: cur.entityId,
          entityType: cur.entityType,
          changedBy: userId,
          status: AssignmentStatus.UNASSIGNED,
          resources: cur.resources ?? [],
        });
      }
      return acc;
    }, [] as BatchUpdateAssignmentInput[]);

  return updateStatusList;
};

const createClassAssignmentInput = (props: {
  classroomId: string;
  userId: string;
  lesson: PlannerCurriculumLesson;
  dateFrom?: string;
  dateTo?: string;
  newMainManifest?: AssignmentManifest[];
  teacherResources?: { studentId: string; resources: string[] }[];
}) => {
  const { lesson, classroomId, userId, teacherResources } = props;

  const classAssignment = {
    entityId: classroomId,
    entityType: Entity.CLASS,
    lessonPlanId: lesson.mainAssignment.lessonPlanId,
    assignor: userId,
    assignmentType: AssignmentType.CURRICULUM,
    clpId: lesson.mainAssignment.clpId || undefined,
    dateFrom: props.dateFrom ? props.dateFrom : lesson.mainAssignment.dateFrom,
    dateTo: props.dateTo ? props.dateTo : lesson.mainAssignment.dateTo,
    manifest: props.newMainManifest
      ? props.newMainManifest
      : lesson.mainAssignment.manifest.map(m => ({
          activityType: m.activityType,
          storyId: m.storyId,
          skills: m.skills?.map(s => ({
            skillId: s.skillId, // Shouldn't need anything other than skillId
          })),
        })),
    assignmentCategory: [AssignmentCategory.CORE], // CORE for district and class assigns anything else for teacher created student assignments
    resources: [
      ...(new Set(
        lesson.resources
          .flatMap(r =>
            r.resources.map(res => res.resourceUniqueId?.toString() || "")
          )
          .filter(id => id !== "")
      ) ?? []),
    ],
  } as CreateAssignmentInput;
  if (
    lesson.mainAssignment.parentId &&
    lesson.mainAssignment.parentId !== "-1"
  ) {
    classAssignment.parentId = lesson.mainAssignment.parentId;
  } else if (
    lesson.mainAssignment.entityType !== Entity.CLASS &&
    lesson.mainAssignment.assignmentId !== "-1"
  ) {
    classAssignment.parentId = lesson.mainAssignment.assignmentId;
  }
  return classAssignment;
};

const createStudentAssignmentBatches = (
  assignments: Partial<Assignment>[],
  resources?: { studentId: string; resources: string[] }[]
) => {
  const assignmentBatches = assignments.reduce((acc, cur) => {
    if (!cur.entityId) {
      return acc;
    }
    const staticCurVal = {
      entityType: cur.entityType,
      grade: cur.grade,
      lessonPlanId: cur.lessonPlanId,
      assignor: cur.assignor,
      assignmentType: cur.assignmentType,
      clpId: cur.clpId || undefined,
      dateFrom: cur.dateFrom,
      dateTo: cur.dateTo,
      manifest: cur.manifest?.map(m => ({
        storyId: m.storyId,
        activityType: m.activityType,
        ...(m.locale !== undefined && { locale: m.locale }),
        ...(m.sequence !== undefined && { sequence: m.sequence }),
        skills: m.skills,
      })),
      assignmentCategory: cur.assignmentCategory,
      resources: resources
        ? (resources.find(r => r.studentId === cur.entityId)?.resources ?? [])
        : (cur.resources ?? []),
    };
    if (acc.length === 0) {
      acc.push({
        ...cur, // assignment blueprint
        entityIdList: [cur.entityId], // student ids that want student assignment blueprint for
        manifest: cur.manifest?.map(m => ({
          storyId: m.storyId,
          activityType: m.activityType,
          ...(m.locale !== undefined && { locale: m.locale }),
          ...(m.sequence !== undefined && { sequence: m.sequence }),
          skills: m.skills,
        })),
      } as any as BatchAddAssignmentInput);
    } else {
      let foundBatch = false;
      acc.forEach(a => {
        const staticCompareVal = {
          entityType: a.entityType,
          grade: a.grade,
          lessonPlanId: a.lessonPlanId,
          assignor: a.assignor,
          assignmentType: a.assignmentType,
          clpId: a.clpId || undefined,
          dateFrom: a.dateFrom,
          dateTo: a.dateTo,
          manifest: a.manifest.map(m => ({
            storyId: m.storyId,
            activityType: m.activityType,
            ...(m.locale !== undefined && { locale: m.locale }),
            ...(m.sequence !== undefined && { sequence: m.sequence }),
            skills: m.skills,
          })),
          assignmentCategory: a.assignmentCategory,
          resources: resources
            ? (resources.find(r => r.studentId === cur.entityId)?.resources ??
              [])
            : (a.resources ?? []),
        };

        // we already have a blueprint that fits this student's assignment add their id to list
        if (isEqual(staticCurVal, staticCompareVal)) {
          a.entityIdList.push(`${cur.entityId}`);
          foundBatch = true;
        }
      });
      // didn't find a blueprint assignment so add a new one with this student id in entityIdList
      if (!foundBatch) {
        acc.push({
          ...cur,
          entityIdList: [cur.entityId],
          resources: resources
            ? (resources.find(r => r.studentId === cur.entityId)?.resources ??
              [])
            : (cur.resources ?? []),
          manifest: cur.manifest?.map(m => ({
            storyId: m.storyId,
            activityType: m.activityType,
            ...(m.locale !== undefined && { locale: m.locale }),
            ...(m.sequence !== undefined && { sequence: m.sequence }),
            skills: m.skills,
          })),
        } as any as BatchAddAssignmentInput);
      }
    }
    return acc;
  }, [] as BatchAddAssignmentInput[]);
  assignmentBatches.forEach(a => {
    if ("entityId" in a) delete (a as any).entityId;
  });
  return assignmentBatches;
};

const getAssignmentCategories = (
  skills: Partial<Skill>[],
  manifest: Partial<AssignmentManifest>[],
  currentCategories?: AssignmentCategory[]
) => {
  const assignmentCategory = [] as AssignmentCategory[];

  // if already has rollover or ai categories then keep those
  if (currentCategories) {
    if (currentCategories.find(c => c === AssignmentCategory.ROLLOVER)) {
      assignmentCategory.push(AssignmentCategory.ROLLOVER);
    }
    if (currentCategories.find(c => c === AssignmentCategory.AI)) {
      assignmentCategory.push(AssignmentCategory.AI);
    }
  }

  // determine core and teacher assigned
  let isCore = false;
  let isTeacherAssigned = false;
  for (let i = 0; i < manifest.length; i++) {
    const story = manifest[i];
    if (story.skills && story.skills.length) {
      const foundIndex = skills.findIndex(
        fs => story.skills && fs.skillId === story.skills[0].skillId
      );
      if (!isCore && foundIndex !== -1) {
        assignmentCategory.push(AssignmentCategory.CORE); // manifest has a core focus skill
        isCore = true;
      }
      if (!isTeacherAssigned && foundIndex === -1) {
        assignmentCategory.push(AssignmentCategory.TEACHER_ASSIGNED); // manifest has a non core focus skill
        isTeacherAssigned = true;
      }

      // early out
      if (isTeacherAssigned && isCore) {
        return assignmentCategory;
      }
    }
  }

  return assignmentCategory;
};

const ensureUniqueStoryIds = <T>(assignmentInput: T): T => {
  if (Array.isArray(assignmentInput)) {
    return assignmentInput.map(input =>
      ensureUniqueStoryIds(input)
    ) as unknown as T;
  }

  if (
    assignmentInput &&
    typeof assignmentInput === "object" &&
    "entityType" in assignmentInput &&
    (assignmentInput as any).entityType === Entity.DISTRICT
  ) {
    return assignmentInput;
  }

  if (
    assignmentInput &&
    typeof assignmentInput === "object" &&
    "manifest" in assignmentInput &&
    Array.isArray((assignmentInput as any).manifest)
  ) {
    const uniqueStoryIds = new Set<string>();
    const uniqueManifest = (assignmentInput as any).manifest.filter(
      (item: any) => {
        // in order to handle skillTutor which will have "00000000-0000-0000-0000-000000000000" as storyId
        // we need to add the skillId to the storyId. The assumption is that one skill may have only one skillTutor resource
        if (
          item.storyId &&
          Array.isArray(item.skills) &&
          item.skills.length > 0 &&
          !uniqueStoryIds.has(`${item.storyId}-${item.skills[0].skillId}`)
        ) {
          uniqueStoryIds.add(`${item.storyId}-${item.skills[0].skillId}`);
          return true;
        }
        return false;
      }
    );
    return {
      ...(assignmentInput as object),
      manifest: uniqueManifest,
    } as T;
  }

  return assignmentInput;
};

const handleStartedAssignments = (props: {
  lesson: PlannerCurriculumLesson;
  userId: string;
  classroomId: string;
  dateFrom?: string;
  dateTo?: string;
  newMainManifest?: AssignmentManifest[];
  newStudentManifests?: { studentId: string; manifest: AssignmentManifest[] }[];
  teacherResources?: { studentId: string; resources: string[] }[];
  originalStudentGroupManifests?: {
    studentId: string;
    manifest: AssignmentManifest[];
  }[];
  removedTeacherResources?: string[];
}) => {
  // When a lesson is started, we must create a new STUDENT assignment
  // Student Inbox service will handle the unassigning of the old assignments
  // and combining the new assignments with the old ones if needed
  const { lesson, userId, classroomId } = props;
  let crudAssignmentsQueue = [] as CRUDAssignmentQueue[];
  let studentAssignmentCreateList = [] as Partial<Assignment>[];
  if (props.newStudentManifests) {
    // also need to add to the list any student manifest changes to the recreate list
    props.newStudentManifests.forEach(sm => {
      const foundAssignment = studentAssignmentCreateList.find(
        assign => assign.entityId === sm.studentId
      );
      const otherSkillsResources = getOtherSkillsTeacherResources(
        lesson,
        props.newStudentManifests,
        props.removedTeacherResources ?? [],
        sm.studentId
      );
      const studentSpecificResources = props.teacherResources?.find(
        tr => tr.studentId.toString() === sm.studentId.toString()
      )?.resources ?? [];
      
      const combinedResources = props.teacherResources
        ? [
            ...new Set([
              ...otherSkillsResources,
              ...studentSpecificResources,
            ]),
          ]
        : [];
      if (foundAssignment) {
        foundAssignment.manifest = sm.manifest;
        foundAssignment.resources = combinedResources.length
          ? combinedResources
          : (foundAssignment.resources ?? []);
      } else {
        const matchedStudentAssign = lesson.studentAssignments.find(
          sa => sa.entityId === sm.studentId
        );
        const manifestToUse =
          matchedStudentAssign?.manifest ?? lesson.mainAssignment.manifest;
        
        // 🔧 FIX: Only skills in the new manifest are being modified  
        // This prevents cross-skill contamination by not including all original skills
        // if the new manifest is empty, use the original manifest
        const modifiedSkillIds =
          sm.manifest.length > 0
            ? new Set(
                sm.manifest.flatMap(m => m.skills?.map(s => s.skillId) || [])
              )
            : new Set(
                props.originalStudentGroupManifests
                  ?.find(osm => osm.studentId === sm.studentId)
                  ?.manifest?.flatMap(m => m.skills?.map(s => s.skillId) || [])
              );
        
        const restOfSkillsManifest = manifestToUse.filter(
          s => !modifiedSkillIds.has(s.skills?.[0]?.skillId)
        );
        const combinedManifest = [...restOfSkillsManifest, ...sm.manifest];
        const newAssign = {
          entityId: sm.studentId,
          entityType: Entity.STUDENT,
          lessonPlanId: lesson.mainAssignment.lessonPlanId,
          assignor: userId,
          assignmentType: combinedManifest.length
            ? AssignmentType.CURRICULUM
            : AssignmentType.SKIP_OVERRIDE,
          clpId: lesson.mainAssignment.clpId || undefined,
          dateFrom: props.dateFrom
            ? props.dateFrom
            : lesson.mainAssignment.dateFrom,
          dateTo: props.dateTo ? props.dateTo : lesson.mainAssignment.dateTo,
          manifest: combinedManifest.length
            ? combinedManifest
            : [
                props.originalStudentGroupManifests?.find(
                  sm => sm.studentId === sm.studentId
                )?.manifest ?? [],
              ],
          assignmentCategory: getAssignmentCategories(
            lesson.skills,
            sm.manifest,
            lesson.mainAssignment.assignmentCategory
          ),
          resources: combinedResources.length ? combinedResources : [],
        } as Partial<Assignment>;
        if (
          lesson.mainAssignment.assignmentId &&
          lesson.mainAssignment.assignmentId !== "-1"
        ) {
          // If no class assignment is being created in this operation, use district assignment ID
          // If a class assignment was created, it would set needToAwaitClassAssignId = true
          newAssign.parentId = lesson.mainAssignment.assignmentId;
        }
        studentAssignmentCreateList.push(newAssign);
      }
    });
  }
  if (studentAssignmentCreateList && studentAssignmentCreateList.length) {
    const otherSkillsTeacherResources = getOtherSkillsTeacherResources(
      lesson,
      props.newStudentManifests,
      props.removedTeacherResources ?? []
      // Note: No studentId here because this is used for batch operations across multiple students
    );
    const mappedOtherSkillsTeacherResources = studentAssignmentCreateList
      .filter(s => s.entityId) // Filter out assignments with undefined entityId
      .map(s => ({
        studentId: s.entityId!,
        resources: otherSkillsTeacherResources,
      }));

    // Combine mappedOtherSkillsTeacherResources and props.teacherResources, ensuring uniqueness
    const combinedTeacherResources = [] as {
      studentId: string;
      resources: string[];
    }[];
    if (props.teacherResources) {
      combinedTeacherResources.push(...mappedOtherSkillsTeacherResources);
      props.teacherResources.forEach(tr => {
        const existingIndex = combinedTeacherResources.findIndex(
          ctr => ctr.studentId === tr.studentId
        );
        if (existingIndex >= 0) {
          // Merge resources for the same studentId, ensuring uniqueness
          const existingResources =
            combinedTeacherResources[existingIndex].resources;
          const mergedResources = [
            ...new Set([...existingResources, ...tr.resources]),
          ];
          combinedTeacherResources[existingIndex] = {
            studentId: tr.studentId,
            resources: mergedResources,
          };
        } else {
          // Add new student resources
          combinedTeacherResources.push(tr);
        }
      });
    }

    const batchCreates = createStudentAssignmentBatches(
      studentAssignmentCreateList,
      combinedTeacherResources
    );
    batchCreates.forEach(batchCreate => {
      crudAssignmentsQueue.push({
        lessonPlanId: lesson.mainAssignment.lessonPlanId,
        crudType: AssignmentCRUDCategory.BatchCreateAssignments,
        assignmentInput: ensureUniqueStoryIds(
          batchCreate
        ) as BatchAddAssignmentInput,
      });
    });
  }

  return crudAssignmentsQueue.map(item => ({
    ...item,
    assignmentInput: ensureUniqueStoryIds(item.assignmentInput),
  }));
};

const handleFutureAssignments = (props: {
  lesson: PlannerCurriculumLesson;
  userId: string;
  classroomId: string;
  dateFrom?: string;
  dateTo?: string;
  newMainManifest?: AssignmentManifest[];
  newStudentManifests?: { studentId: string; manifest: AssignmentManifest[] }[];
  teacherResources?: { studentId: string; resources: string[] }[];
  originalStudentGroupManifests?: {
    studentId: string;
    manifest: AssignmentManifest[];
  }[];
  removedTeacherResources?: string[];
}) => {
  
  // *IN GENERAL - Future means never unassign, only create if assignment does not exist, update if assignment does exist
  const { lesson, userId, classroomId } = props;
  let crudAssignmentsQueue = [] as CRUDAssignmentQueue[];
  let awaitingClassAssignmentId = false;

  if (!props.dateFrom && !props.newMainManifest && !props.newStudentManifests) {
    return crudAssignmentsQueue;
  }

  // *Class Assignment - update or create
  // changed to ensure there is always a class assignment when teacher edits a lesson even at student level
  // if need to switch back to not creating a class assignment on only student level changes remove the following:
  // || (props.newStudentManifests && lesson.mainAssignment.entityType !== Entity.CLASS)
  if (
    props.newMainManifest ||
    props.dateFrom
    /*||
    (props.newStudentManifests &&
      lesson.mainAssignment.entityType !== Entity.CLASS)*/
  ) {
    
    if (lesson.mainAssignment.entityType === Entity.CLASS) {
      // update class assign
      const assignId = lesson.mainAssignment.assignmentId;
      if (assignId) {
        // ADD UPDATE CLASS ASSIGN QUEUE
        const assignInput = {
          entityType: Entity.CLASS,
          entityId: classroomId,
          assignmentId: lesson.mainAssignment.assignmentId,
        } as BatchUpdateAssignmentInput;
        if (props.dateFrom) {
          assignInput.dateFrom = props.dateFrom;
        }
        if (props.dateTo) {
          assignInput.dateTo = props.dateTo;
        }
        if (props.newMainManifest) {
          assignInput.manifest = props.newMainManifest;
        }
        crudAssignmentsQueue.push({
          lessonPlanId: lesson.mainAssignment.lessonPlanId,
          crudType: AssignmentCRUDCategory.BatchUpdateAssignments,
          assignmentInput: [assignInput] as BatchUpdateAssignmentInput[],
        });
      }
    } else {
      // create class assign
      awaitingClassAssignmentId = true;
      const classAssignment = createClassAssignmentInput({
        userId,
        classroomId,
        lesson,
        dateFrom: props.dateFrom,
        dateTo: props.dateTo,
        newMainManifest: props.newMainManifest,
        teacherResources: props.teacherResources,
      });
      // ADD CREATE CLASS ASSIGN QUEUE
      crudAssignmentsQueue.push({
        lessonPlanId: lesson.mainAssignment.lessonPlanId,
        crudType: AssignmentCRUDCategory.CreateAssignment,
        assignmentInput: classAssignment as CreateAssignmentInput,
      });
    }
  }

  // *Student Assignments - updates
  let studentUpdateList = [] as BatchUpdateAssignmentInput[];
  if (props.dateFrom) {
    // update all existing student assignments
    studentUpdateList = lesson.studentAssignments.map(sa => {
      const batchInput = {
        assignmentId: sa.assignmentId,
        entityId: sa.entityId,
        entityType: sa.entityType,
        dateFrom: props.dateFrom,
        dateTo: props.dateTo,
      } as BatchUpdateAssignmentInput;
      if (props.newStudentManifests && props.newStudentManifests.length) {
        const foundNewManifest = props.newStudentManifests.find(
          sm => sm.studentId === sa.entityId
        );
        if (foundNewManifest) {
          batchInput.manifest = foundNewManifest.manifest;
        }
      }
      return batchInput;
    });
  } else if (props.newStudentManifests && props.newStudentManifests.length) {
    const unassignAssignments = [] as Partial<Assignment>[];
    // update specific student assignments
    props.newStudentManifests.forEach(sm => {
      if (!sm.manifest.length) return;
      const foundAssignment = lesson.studentAssignments.find(
        sa =>
          sa.entityId === sm.studentId &&
          sa.assignmentType !== AssignmentType.SKIP_OVERRIDE
      );
      if (foundAssignment) {        
        // 🔧 FIX: Only skills in the new manifest are being modified
        // This prevents cross-skill contamination by not including all original skills
        // if the new manifest is empty, use the original manifest
        const modifiedSkillIds =
          sm.manifest.length > 0
            ? new Set(
                sm.manifest.flatMap(m => m.skills?.map(s => s.skillId) || [])
              )
            : new Set(
                props.originalStudentGroupManifests
                  ?.find(osm => osm.studentId === sm.studentId)
                  ?.manifest?.flatMap(m => m.skills?.map(s => s.skillId) || [])
              );
        
        const otherSkillsManifest = foundAssignment.manifest
          .filter(s => !modifiedSkillIds.has(s.skills?.[0]?.skillId))
          .map(s => ({
            activityType: s.activityType,
            storyId: s.storyId,
            skills: [{ skillId: s.skills?.[0]?.skillId }],
          }));
        
        studentUpdateList.push({
          assignmentId: foundAssignment.assignmentId,
          entityId: foundAssignment.entityId,
          entityType: foundAssignment.entityType,
          manifest: [...otherSkillsManifest, ...sm.manifest],
          assignmentCategory: getAssignmentCategories(
            lesson.skills,
            sm.manifest,
            foundAssignment.assignmentCategory
          ),
          resources: props.teacherResources
            ? [
                ...new Set([
                  ...getOtherSkillsTeacherResources(
                    lesson,
                    props.newStudentManifests,
                    props.removedTeacherResources ?? [],
                    foundAssignment.entityId
                  ),
                  ...(props.teacherResources.find(
                    tr => tr.studentId === foundAssignment.entityId
                  )?.resources ?? []),
                ]),
              ]
            : (foundAssignment.resources ?? []),
        } as BatchUpdateAssignmentInput);
      }
    });
    // if there are student assignments records and we want to remove all student assignments
    // then unassign them and use create assignment to create new student assignments with assignment type SKIP_OVERRIDE.
    // Or if the student assignment type is SKIP_OVERRIDE then unassign them
    // and use create assignment to create new student assignments with assignment type CURRICULUM
    if (
      studentUpdateList.length === 0 &&
      (props.newStudentManifests?.some(sm => sm.manifest.length === 0) ||
        props.newStudentManifests?.some(
          sm =>
            lesson.studentAssignments.find(sa => sa.entityId === sm.studentId)
              ?.assignmentType === AssignmentType.SKIP_OVERRIDE
        ))
    ) {
      const stuAssigns = lesson.studentAssignments.filter(
        sa => sa.status === AssignmentStatus.ASSIGNED
      );

      props.newStudentManifests.forEach(sm => {
        const studAssign = stuAssigns.find(sa => sa.entityId === sm.studentId);
        if (studAssign) {
          unassignAssignments.push(studAssign);
        }
      });
              const unassignList = handleUnassignAssignments(
          unassignAssignments,
          userId
        );
      if (unassignList.length) {
        // ADD UNASSIGN BATCH TO QUEUE FIRST
        crudAssignmentsQueue.push({
          lessonPlanId: lesson.mainAssignment.lessonPlanId,
          crudType: AssignmentCRUDCategory.BatchUnassignAssignments,
          assignmentInput: unassignList as BatchUpdateAssignmentInput[],
        } as CRUDAssignmentQueue);
      }
    }
  }
  if (studentUpdateList.length) {
    if (
      crudAssignmentsQueue.length &&
      crudAssignmentsQueue[crudAssignmentsQueue.length - 1].crudType ===
        AssignmentCRUDCategory.BatchUpdateAssignments
    ) {
      crudAssignmentsQueue[crudAssignmentsQueue.length - 1].assignmentInput = [
        ...(crudAssignmentsQueue[crudAssignmentsQueue.length - 1]
          .assignmentInput as BatchUpdateAssignmentInput[]),
        ...(studentUpdateList as BatchUpdateAssignmentInput[]),
      ];
    } else {
      crudAssignmentsQueue.push({
        lessonPlanId: lesson.mainAssignment.lessonPlanId,
        crudType: AssignmentCRUDCategory.BatchUpdateAssignments,
        assignmentInput: studentUpdateList as BatchUpdateAssignmentInput[],
      });
    }
  }
  // *Student Assignments - creates
  if (props.newStudentManifests && props.newStudentManifests.length) {
    // create new student assignments as needed
    const newCreateStudentAssignments = props.newStudentManifests
      .filter(
        sm =>
          studentUpdateList.findIndex(su => su.entityId === sm.studentId) === -1
      )
      .map(sm => {
        
        const matchedStudentAssign = lesson.studentAssignments.find(
          sa => sa.entityId === sm.studentId
        );
        const manifestToUse =
          matchedStudentAssign?.manifest ?? lesson.mainAssignment.manifest;
          
        // For student assignments, only include manifest items from other skills
        // The current skill's manifest is completely replaced by sm.manifest
        // Get the skillIds that were modified by comparing original with new manifest
        
        // 🔧 FIX: Only skills in the new manifest are being modified
        // This prevents cross-skill contamination by not including all original skills
        // if the new manifest is empty, use the original manifest
        const modifiedSkillIds =
          sm.manifest.length > 0
            ? new Set(
                sm.manifest.flatMap(m => m.skills?.map(s => s.skillId) || [])
              )
            : new Set(
                props.originalStudentGroupManifests
                  ?.find(osm => osm.studentId === sm.studentId)
                  ?.manifest?.flatMap(m => m.skills?.map(s => s.skillId) || [])
              );
        
        const restOfSkillsManifest = manifestToUse.filter(
          s => !modifiedSkillIds.has(s.skills?.[0]?.skillId)
        );
        const combinedManifest = [...restOfSkillsManifest, ...sm.manifest];
        
        const assign = {
          entityId: sm.studentId,
          entityType: Entity.STUDENT,
          dateFrom: props.dateFrom
            ? props.dateFrom
            : lesson.mainAssignment.dateFrom,
          dateTo: props.dateTo ? props.dateTo : lesson.mainAssignment.dateTo,
          lessonPlanId: lesson.mainAssignment.lessonPlanId,
          assignor: userId,
          assignmentType: combinedManifest.length
            ? AssignmentType.CURRICULUM
            : AssignmentType.SKIP_OVERRIDE,
          clpId: lesson.mainAssignment.clpId || undefined,
          assignmentCategory: getAssignmentCategories(
            lesson.skills,
            sm.manifest,
            lesson.mainAssignment.assignmentCategory
          ),
          manifest: combinedManifest.length
            ? combinedManifest
            : [(props.originalStudentGroupManifests?.find(
                sm => sm.studentId === sm.studentId
              )?.manifest ?? [])],
          resources: (() => {
            const otherSkillsResources = getOtherSkillsTeacherResources(
              lesson,
              props.newStudentManifests,
              props.removedTeacherResources ?? [],
              sm.studentId
            );
            const studentSpecificResources = props.teacherResources?.find(
              tr => tr.studentId.toString() === sm.studentId.toString()
            )?.resources ?? [];
            
            return props.teacherResources
              ? [
                  ...new Set([
                    ...otherSkillsResources,
                    ...studentSpecificResources,
                  ]),
                ]
              : [];
          })(),
        } as Partial<Assignment>;

        if (
          (lesson.mainAssignment.entityType !== Entity.CLASS ||
            !awaitingClassAssignmentId) &&
          lesson.mainAssignment.assignmentId !== "-1"
        ) {
          assign.parentId = lesson.mainAssignment.assignmentId;
        }

        return assign;
      });
    if (newCreateStudentAssignments.length) {
      const batchCreates = createStudentAssignmentBatches(
        newCreateStudentAssignments,
        // we don't need to pass teacher resources here because, because they are already in newCreateStudentAssignments
      );
      batchCreates.forEach(batchCreate => {
        crudAssignmentsQueue.push({
          lessonPlanId: lesson.mainAssignment.lessonPlanId,
          crudType: AssignmentCRUDCategory.BatchCreateAssignments,
          assignmentInput: ensureUniqueStoryIds(
            batchCreate
          ) as BatchAddAssignmentInput,
        });
      });
    }
  }

  return crudAssignmentsQueue.map(item => ({
    ...item,
    assignmentInput: ensureUniqueStoryIds(item.assignmentInput),
  }));
};

export const handleCreateUpdateAssignments = (props: {
  lesson: PlannerCurriculumLesson;
  userId: string;
  classroomId: string;
  dateFrom?: string;
  dateTo?: string;
  newMainManifest?: AssignmentManifest[];
  newStudentManifests?: { studentId: string; manifest: AssignmentManifest[] }[];
  teacherResources?: { studentId: string; resources: string[] }[];
  originalStudentGroupManifests?: {
    studentId: string;
    manifest: AssignmentManifest[];
  }[]; // this will be used if newStudentManifests is empty
  removedTeacherResources?: string[]; // this is used to remove shared teacher resources from the resources array
}) => {
    const { lesson } = props;

  const lessonDate = startOfDay(new Date(lesson.dateFrom));
  const todayDate = startOfDay(Date.now());
  const isLessonStarted =
    isBefore(lessonDate, todayDate) ||
    lessonDate.getTime() === todayDate.getTime();
  
  // make sure to remove teacher resources from the manifest if any
  const updatedProps = {
    ...props,
    newStudentManifests: props.newStudentManifests?.map(sm => ({
      ...sm,
      manifest: sm.manifest.filter(
        m => m.activityType !== ResourceType.teacherResource
      ),
    })),
  };
  
  const crudAssignmentsQueue = isLessonStarted
    ? handleStartedAssignments(updatedProps)
    : handleFutureAssignments(updatedProps);

  return crudAssignmentsQueue.length ? crudAssignmentsQueue : null;
};

export const getManifestByResourceType = (
  r: AmiraResources,
  skillId: string
) => {
  const manifest: AssignmentManifest[] = [];
  switch (r.resourceType) {
    case ResourceType.microLesson:
    case ResourceType.istationLegacyActivity:
    case ResourceType.tutor:
      manifest.push({
        storyId: r.resourceUniqueId,
        activityType: r.resourceType,
        skills: [{ skillId }] as Skill[],
      });
      break;
    case ResourceType.skillTutor:
      manifest.push({
        storyId: "00000000-0000-0000-0000-000000000000",
        activityType: ResourceType.tutor,
        skills: [{ skillId }] as Skill[],
      });
      break;
    case ResourceType.EPS:
    case ResourceType.ERSS:
    case ResourceType.textSet:
      if (r.storyIds) {
        r.storyIds.forEach(storyId => {
          manifest.push({
            storyId,
            activityType: ResourceType.tutor,
            skills: [{ skillId }] as Skill[],
          });
        });
      }
      break;
    default:
      break;
  }
  return manifest;
};

export const getDefaultManifest = (
  resources: { skillId: string; resources: AmiraResources[] }[],
  skills: Skill[]
) => {
  const sortedSkills = skills.sort((a, b) =>
    a.sequence && b.sequence ? a.sequence - b.sequence : 0
  );
  const manifest: AssignmentManifest[] = [];

  sortedSkills.forEach(s => {
    const matchedResources = resources.find(
      r => r.skillId === s.skillId
    )?.resources;
    if (matchedResources) {
      matchedResources.forEach(r => {
        manifest.push(...getManifestByResourceType(r, s.skillId!));
      });
    }
  });

  // Use ensureUniqueStoryIds to filter out duplicate storyIds
  return ensureUniqueStoryIds({ manifest }).manifest as AssignmentManifest[];
};
